import { createContext, useContext, useState, ReactNode } from 'react';
import { Ride, UpdateRideStatusInput } from '@ouagaliv/shared';
import { api } from '../lib/api';

interface RideContextType {
  currentRide: Ride | null;
  availableRides: Ride[];
  isLoading: boolean;
  getRide: (rideId: string) => Promise<Ride>;
  fetchAvailableRides: (lat: number, lng: number, radius?: number) => Promise<Ride[]>;
  acceptRide: (rideId: string) => Promise<Ride>;
  updateRideStatus: (rideId: string, data: UpdateRideStatusInput) => Promise<Ride>;
  declineRide: (rideId: string) => void;
  setCurrentRide: (ride: Ride | null) => void;
}

const RideContext = createContext<RideContextType | undefined>(undefined);

export function RideProvider({ children }: { children: ReactNode }) {
  const [currentRide, setCurrentRide] = useState<Ride | null>(null);
  const [availableRides, setAvailableRides] = useState<Ride[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getRide = async (rideId: string): Promise<Ride> => {
    const res = await api.get<{ success: boolean; data: Ride }>(`/rides/${rideId}`);
    if (!res.data.success) throw new Error('Failed to get ride');
    const ride = res.data.data;
    setCurrentRide(ride);
    return ride;
  };

  const fetchAvailableRides = async (lat: number, lng: number, radius: number = 500): Promise<Ride[]> => {
    setIsLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: Ride[] }>(`/rides/nearby?lat=${lat}&lng=${lng}&radius=${radius}`);
      if (!res.data.success) throw new Error('Failed to fetch rides');
      const rides = res.data.data;
      setAvailableRides(rides);
      return rides;
    } finally {
      setIsLoading(false);
    }
  };

  const acceptRide = async (rideId: string): Promise<Ride> => {
    const res = await api.post<{ success: boolean; data: Ride }>(`/rides/${rideId}/accept`);
    if (!res.data.success) throw new Error('Failed to accept ride');
    const ride = res.data.data;
    setCurrentRide(ride);
    setAvailableRides(prev => prev.filter(r => r.id !== rideId));
    return ride;
  };

  const updateRideStatus = async (rideId: string, data: UpdateRideStatusInput): Promise<Ride> => {
    const res = await api.patch<{ success: boolean; data: Ride }>(`/rides/${rideId}/status`, data);
    if (!res.data.success) throw new Error('Failed to update ride status');
    const ride = res.data.data;
    setCurrentRide(ride);
    return ride;
  };

  const declineRide = (rideId: string): void => {
    // Refus côté agent : la course reste disponible pour les autres livreurs,
    // le moteur de matching progressif se charge de la reproposer.
    setAvailableRides(prev => prev.filter(r => r.id !== rideId));
  };

  return (
    <RideContext.Provider value={{ currentRide, availableRides, isLoading, getRide, fetchAvailableRides, acceptRide, updateRideStatus, declineRide, setCurrentRide }}>
      {children}
    </RideContext.Provider>
  );
}

export function useRide() {
  const context = useContext(RideContext);
  if (!context) throw new Error('useRide must be used within RideProvider');
  return context;
}